import { css } from "@emotion/css";
import { ExternalLink } from "@octopusdeploy/design-system-components";
import { space, themeTokens, lineHeight, borderWidth } from "@octopusdeploy/design-system-tokens";
import cn from "classnames";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import InternalLink from "../Navigation/InternalLink";
import { ansiClassesDark, ansiClassesLight } from "../TextWithLinks/AnsiColors";
import { AppRelativeLinkPattern, isExternalUrl, isInternalUrl } from "../TextWithLinks/parseLinks";
import { useThemePaletteType } from "../Theme/useThemePaletteType";
import { ansiSanitizeSchema, rehypeAnsiColors } from "./ansiMarkdownSupport";
interface MarkdownProps {
    markup: string;
    mutedTextColor?: boolean;
    noMargins?: boolean;
}
function Markdown({ markup, mutedTextColor, noMargins }: MarkdownProps) {
    const parseAnsiColors = useOctopusFeatureToggle("ansi-colors", false);
    const isDarkMode = useThemePaletteType() === "dark";
    return (<div className={cn(styles.base, {
            [styles.mutedTextColorStyling]: mutedTextColor,
            [styles.noMarginsStyling]: noMargins,
            [isDarkMode ? ansiClassesDark : ansiClassesLight]: parseAnsiColors,
        })}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={parseAnsiColors ? [rehypeRaw, rehypeAnsiColors, [rehypeSanitize, ansiSanitizeSchema]] : [rehypeRaw, rehypeSanitize]} components={{
            a: ({ href, children, node, ...props }) => {
                if (href && isInternalUrl(href)) {
                    const internalUrl = href.replace(AppRelativeLinkPattern, "");
                    return <InternalLink to={internalUrl}>{children}</InternalLink>;
                }
                else if (href && isExternalUrl(href) && typeof children === "string") {
                    return <ExternalLink href={href}>{children}</ExternalLink>;
                }
                else if (href && isExternalUrl(href)) {
                    // fall back to generic anchor when children are JSX/ReactNodes
                    return (<a href={href} target="_blank" rel="noopener noreferrer">
                                    {children}
                                </a>);
                }
                return (<a href={href} rel="noopener noreferrer" target="_blank" {...props}>
                                {children}
                            </a>);
            },
        }}>
                {markup}
            </ReactMarkdown>
        </div>);
}
export default Markdown;
const listPaddingLeft = space[40];
const styles = {
    base: css({
        lineHeight: lineHeight.xSmall,
        "& .list": {
            paddingLeft: listPaddingLeft,
        },
        ul: {
            listStyleType: "disc",
            paddingLeft: listPaddingLeft,
        },
        ol: {
            listStyleType: "decimal",
            paddingLeft: listPaddingLeft,
        },
        blockquote: {
            borderLeft: [borderWidth["4"], "solid", themeTokens.color.border.primary].join(" "),
            paddingLeft: space[16],
        },
    }),
    mutedTextColorStyling: css({
        p: {
            color: themeTokens.color.text.secondary,
        },
    }),
    noMarginsStyling: css({
        p: {
            margin: 0,
        },
    }),
};
